import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { OrderType, ReportsSendToEmail } from '../@core/constants';
import { DigitalSignature } from '../models/digital-signature';
import { Filter } from '../models/filter';
import { OrderFilter } from '../models/filters/order-filter';
import { Order } from '../models/order';
import { OrderCompleteProcess } from '../models/order-complete-process';
import { OrdersWithPendingInvoice } from '../models/order-with-pending-invoice';
import { Paging } from '../models/page';
import { BiometryUnlockRequest } from '../models/request/biometry-unlock-request';
import { LoanSimulationRequest } from '../models/requests/loan-simulation-request';
import { OrderRequest } from '../models/requests/order-request';
import { RejectOrderRequest } from '../models/requests/reject-order-request';
import { ResponseModel } from '../models/response-model';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class OrderService {

  private url = `${environment.url}/orders`;

  constructor(private http: HttpClient, private configService: ConfigurationService) {


  }

  readOrders(filter: Filter<OrderFilter>): Observable<ResponseModel<Paging<Array<Order>>>> {

    let params = new HttpParams();
    params = params
      .set("page", filter.page.toString() || "0")
      .set("pageSize", filter.pageSize.toString() || "20")
      .set("salesOrgCode", this.configService.readSelectedBranchAndSalesOrg().salesOrg.code);

    params = filter.value.text ? params.set("text", filter.value.text) : params;
    params = filter.value.fiscalId ? params.set("fiscalId", filter.value.fiscalId) : params;
    params = filter.value.customerName ? params.set("customerName", filter.value.customerName) : params;
    params = filter.value.customerLastName ? params.set("customerLastName", filter.value.customerLastName) : params;
    params = filter.value.salesOrgList ? params.set("salesOrgList", filter.value.salesOrgList) : params;
    params = filter.value.regionCode ? params.set("regionCode", filter.value.regionCode) : params;
    params = filter.value.branchCode ? params.set("branch", filter.value.branchCode) : params;
    params = filter.value.customerCode ? params.set("customer", filter.value.customerCode) : params;
    params = filter.value.sellerCode ? params.set("vendor", filter.value.sellerCode) : params;
    params = filter.value.invoiceNumber ? params.set("invoiceNumber", filter.value.invoiceNumber) : params;
    if (filter.value.orderType != OrderType.RETURN) {
      params = filter.value.status ? params.set("status", filter.value.status) : params;
    }
    params = filter.value.end ? params.set("end", filter.value.end) : params;
    params = filter.value.start ? params.set("start", filter.value.start) : params;
    params = filter.value.isFranchised ? params.set("isFranchised", String(filter.value.isFranchised)) : params;
    params = filter.value.orderType ? params.set("orderType", filter.value.orderType) : params.set("orderType", `${OrderType.REGULAR},${OrderType.QUOTATION},${OrderType.REPAIRSHOP}`);
    params = filter.value.condition ? params.set("condition", filter.value.condition) : params;
    params = filter.value.digitalSignatureStatus ? params.set("digitalSignatureStatus", filter.value.digitalSignatureStatus) : params;
    params = filter.value.includeCreditNotes != undefined ? params.set("includeCreditNotes", String(filter.value.includeCreditNotes)) : params;
    params = filter.value.isDummy != undefined ? params.set("isDummy", String(filter.value.isDummy)) : params;
    params = filter.value.checkedStatus ? params.set("checkedStatus", filter.value.checkedStatus) : params;


    return this.http.get<ResponseModel<Paging<Array<Order>>>>(this.url, { params });
  }

  createOrderSimulate(
    orderRequest: OrderRequest
  ): Observable<ResponseModel<Order>> {
    return this.createOrder(orderRequest, true);
  }

  createOrder(
    orderRequest: OrderRequest,
    simulate: boolean
  ): Observable<ResponseModel<Order>> {
    let params = new HttpParams();
    params = params.set('simulate', simulate ? 'true' : 'false');
    return this.http.post<ResponseModel<Order>>(this.url, orderRequest, {
      params: params,
    });
  }

  createOrderCompleteProcess(
    orderRequest: OrderRequest,
    loanSimulationRequest: Array<LoanSimulationRequest>
  ): Observable<ResponseModel<OrderCompleteProcess>> {
    console.log(`order request desde el servicio ===>`, orderRequest);
    let body = {
      order: orderRequest,
      loan: loanSimulationRequest,
      origin: "RETAIL"
    };

    return this.http.post<ResponseModel<OrderCompleteProcess>>(
      `${this.url}/complete`,
      body
    );
  }

  updateOrderStatus(
    orderCode: string,
    rejectOrderRequest: RejectOrderRequest
  ): Observable<ResponseModel<Order>> {
    return this.http.put<ResponseModel<Order>>(
      `${this.url}/${orderCode}/modifyStatus`,
      rejectOrderRequest
    );
  }

  readOrder(orderCode: string): Observable<ResponseModel<Order>> {
    return this.http.get<ResponseModel<Order>>(`${this.url}/${orderCode}`);
  }

  readerOrderCompleteProcess(
    orderCode: string
  ): Observable<ResponseModel<OrderCompleteProcess>> {
    return this.http.get<ResponseModel<OrderCompleteProcess>>(
      `${this.url}/${orderCode}/complete`
    );
  }

  updateOrderCompleteProcess(
    orderCode: string,
    orderRequest: OrderRequest,
    loanSimulationRequest: Array<LoanSimulationRequest>
  ): Observable<ResponseModel<OrderCompleteProcess>> {


    let body = {
      order: orderRequest,
      loan: loanSimulationRequest,
      origin: "RETAIL"
    };

    return this.http.put<ResponseModel<OrderCompleteProcess>>(
      `${this.url}/${orderCode}/complete`,
      body
    );
  }

  simulateOrderWithLoans(
    orderRequest: OrderRequest
  ): Observable<ResponseModel<OrderCompleteProcess>> {
    return this.http.post<ResponseModel<OrderCompleteProcess>>(
      `${this.url}/complete/simulate`,
      orderRequest
    );
  }

  readDigitalSignatureByOrderCode(orderCode: string): Observable<DigitalSignature> {
    return this.http
      .get<ResponseModel<DigitalSignature>>(`${this.url}/${orderCode}/digitalSignature`, {})
      .pipe(map((response: ResponseModel<DigitalSignature>) => response.response));
  }

  createSigningRequest(orderCode: string): Observable<ResponseModel<DigitalSignature>> {
    return this.http.post<ResponseModel<DigitalSignature>>(`${this.url}/${orderCode}/createSigningRequest`, {});
  }

  signDocuments(requestCode: number, otpCode: string): Observable<ResponseModel<DigitalSignature>> {
    let params = new HttpParams();
    params = params
      .set("requestCode", requestCode.toString())
      .set("otpCode", otpCode);

    return this.http.post<ResponseModel<DigitalSignature>>(`${this.url}/signDocuments`, {}, { params });
  }

  unlockOrderByCode(orderCode: string, unlockNotes: string, token?: string) {
    let headers = new HttpHeaders();

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    let url = `${this.url}/${orderCode}/unlock`;

    return this.http.put(url, unlockNotes, { headers });
  }

  getPriceForVehicleRegister(productCode: string, branchCode: string): Observable<ResponseModel<number>> {
    let params = new HttpParams();
    params = params
      .set("productCode", productCode)
      .set("branchCode", branchCode);

    return this.http.get<ResponseModel<number>>(`${this.url}/priceEquipmentRegistration`, { params });
  }

  getOrderForCreditReference(customerCode: string, salesOrgCode: string, branchCode: string): Observable<ResponseModel<Order>> {
    let params = new HttpParams();
    params = params
      .set('salesOrgCode', salesOrgCode)
      .set('branchCode', branchCode)
      .set('customerCode', customerCode);

    return this.http.get<ResponseModel<Order>>(`${this.url}/createOrderForCreditReference`, { params });
  }

  readOrderWithPendingInvoice(filter: Filter<any>, salesOrgCode: string, branchCode: string): Observable<ResponseModel<Paging<Array<OrdersWithPendingInvoice>>>> {
    let url = this.url + "/ordersWithPendingInvoice";

    let params = new HttpParams();
    params = params
      .set("branchCode", branchCode)
      .set("salesOrgCode", salesOrgCode)
      .set("page", filter.page.toString() || "0")
      .set("pageSize", filter.pageSize.toString() || "20");

    return this.http.get<ResponseModel<Paging<Array<OrdersWithPendingInvoice>>>>(`${url}`, { params });
  }

  sendEmail(orderCode: string, reportsSendToEmail: Array<ReportsSendToEmail>) {
    let params = new HttpParams();
    reportsSendToEmail.forEach(f => {
      params = params.append("reportsSendToEmail", f);
    })

    return this.http.post<ResponseModel<number>>(`${this.url}/${orderCode}/sendEmail`, undefined, { params });
  }

  updateBiometryStatus(orderCode: string, request: BiometryUnlockRequest) {
    return this.http.put<ResponseModel<Order>>(`${this.url}/${orderCode}/updateBiometryStatus`, request);
  }

  unlockBiometryOrder(orderCode: string, request: BiometryUnlockRequest, token?: string) {
    let headers = new HttpHeaders();

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    let url = `${this.url}/${orderCode}/unlockBiometry`;

    return this.http.put(url, request, { headers });
  }
  rejectBiometryOrder(orderCode: string, request: BiometryUnlockRequest, token?: string) {
    let headers = new HttpHeaders();

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    let url = `${this.url}/${orderCode}/rejectBiometry`;

    return this.http.put(url, request, { headers });
  }
}
